import type VectorLayer from 'ol/layer/Vector'
import type VectorSource from 'ol/source/Vector'

import { createNonBlockingRenderByTime } from '../../renderingQueue'
import { getLayerMetadata, setLayerMetadata } from '../layerMetadata'

import { findFeature } from '../findFeature'

import { updateDeviceFeature } from './utils/updateDeviceFeature'
import { findDeviceMarker } from './utils/findDeviceMarker'

const nonblockingRender = createNonBlockingRenderByTime()

export const updateDevicesMapSelection = async (
  layer: VectorLayer<VectorSource>,
  mapMarkers: uui.domain.ui.map.markers.MapMarkers['device'],
  mapStyles: uui.domain.ui.map.markers.MapStyles['device'],
  selection: Set<string>,
) => {
  // retrieve selected items in the current layer
  const layerSelection = (getLayerMetadata(layer, 'selection') ?? new Set()) as Set<string>

  // if prev and next are empty do nothing
  if (selection.size !== 0 || layerSelection.size !== 0) {
    const devicesToDeselect = new Set(layerSelection)

    nonblockingRender.reset()

    for (const markerId of selection) {
      await nonblockingRender.next()

      // remove it from deviceId to be deselected
      devicesToDeselect.delete(markerId)

      if (layerSelection.has(markerId)) {
        // deviceId was previously selected and it's still selected
        // do nothing
        continue
      }

      const marker = findDeviceMarker(mapMarkers, markerId)
      // It is possible that a marker doesn't exists on the map since it can be possible that no live position is available
      if (!marker) continue

      const feature = findFeature(layer, marker.id)

      if (!feature) {
        throw new Error(
          `Trying to select Device marker with lat-lng: ${markerId} for ID: ${markerId} but no feature exist on the map`,
        )
      }

      // the deviceId is a new selection
      updateDeviceFeature(mapStyles, marker, feature, true)
    }

    nonblockingRender.reset()

    for (const markerId of devicesToDeselect) {
      await nonblockingRender.next()

      const marker = findDeviceMarker(mapMarkers, markerId)
      // It is possible that a marker doesn't exists on the map since it can be possible that no live position is available
      if (!marker) continue

      const feature = findFeature(layer, marker.id)

      if (!feature) {
        // fail silently in production when deselecting unknown items
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `Trying to deselect Device marker with lat-lng: ${markerId} for ID: ${markerId} but no feature exist on the map`,
          )
        }

        continue
      }

      // deselect the device
      updateDeviceFeature(mapStyles, marker, feature, false)
    }
  }

  // update the stored collection
  setLayerMetadata(layer, 'selection', selection)
}
